import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Plus, Trash2, Save, Search, GripVertical, X, ToggleLeft, ToggleRight, ArrowLeft } from 'lucide-react';
import Header from '../../components/ui/Header';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const parseVerseRef = (input) => {
  // Accepts "John 3:16", "Gen 1:1", "1 Cor 13:4" etc.
  const match = input?.trim()?.match(/^(.+?)\s+(\d+):(\d+)$/);
  if (!match) return null;
  return { book: match?.[1]?.trim(), chapter: parseInt(match?.[2]), verse: parseInt(match?.[3]) };
};

const bookAbbrevMap = {
  'genesis': 'GEN', 'exodus': 'EXO', 'leviticus': 'LEV', 'numbers': 'NUM', 'deuteronomy': 'DEU',
  'joshua': 'JOS', 'judges': 'JDG', 'ruth': 'RUT', '1 samuel': '1SA', '2 samuel': '2SA',
  '1 kings': '1KI', '2 kings': '2KI', '1 chronicles': '1CH', '2 chronicles': '2CH',
  'ezra': 'EZR', 'nehemiah': 'NEH', 'esther': 'EST', 'job': 'JOB', 'psalms': 'PSA', 'psalm': 'PSA',
  'proverbs': 'PRO', 'ecclesiastes': 'ECC', 'song of solomon': 'SNG', 'isaiah': 'ISA',
  'jeremiah': 'JER', 'lamentations': 'LAM', 'ezekiel': 'EZK', 'daniel': 'DAN',
  'hosea': 'HOS', 'joel': 'JOL', 'amos': 'AMO', 'obadiah': 'OBA', 'jonah': 'JON',
  'micah': 'MIC', 'nahum': 'NAH', 'habakkuk': 'HAB', 'zephaniah': 'ZEP', 'haggai': 'HAG',
  'zechariah': 'ZEC', 'malachi': 'MAL', 'matthew': 'MAT', 'mark': 'MRK', 'luke': 'LUK',
  'john': 'JHN', 'acts': 'ACT', 'romans': 'ROM', '1 corinthians': '1CO', '2 corinthians': '2CO',
  'galatians': 'GAL', 'ephesians': 'EPH', 'philippians': 'PHP', 'colossians': 'COL',
  '1 thessalonians': '1TH', '2 thessalonians': '2TH', '1 timothy': '1TI', '2 timothy': '2TI',
  'titus': 'TIT', 'philemon': 'PHM', 'hebrews': 'HEB', 'james': 'JAS',
  '1 peter': '1PE', '2 peter': '2PE', '1 john': '1JN', '2 john': '2JN', '3 john': '3JN',
  'jude': 'JUD', 'revelation': 'REV'
};

const getAbbrev = (bookName) => {
  const key = bookName?.toLowerCase();
  return bookAbbrevMap?.[key] || bookName?.toUpperCase()?.slice(0, 3);
};

// ─── AddVersePanel ─────────────────────────────────────────────────────────────
const AddVersePanel = ({ onAdd, onCancel }) => {
  const [query, setQuery] = useState('');
  const [preview, setPreview] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const handleSearch = async () => {
    const parsed = parseVerseRef(query);
    if (!parsed) {
      setSearchError('Use format: "Book Chapter:Verse" e.g. "John 3:16"');
      return;
    }
    setSearching(true);
    setSearchError('');
    setPreview(null);
    try {
      const abbrev = getAbbrev(parsed?.book);
      const { data, error } = await supabase?.from('bible_verses')?.select('book_name, book_abbrev, chapter, verse, text')?.ilike('book_name', `%${parsed?.book}%`)?.eq('chapter', parsed?.chapter)?.eq('verse', parsed?.verse)?.eq('bible_version', 'KJV')?.limit(1)?.maybeSingle();

      if (error || !data) {
        setSearchError('Verse not found. Check the reference and try again.');
      } else {
        setPreview(data);
      }
    } catch (err) {
      setSearchError('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleConfirm = () => {
    if (!preview) return;
    const abbrev = preview?.book_abbrev || getAbbrev(preview?.book_name);
    onAdd({
      book_abbrev: abbrev,
      chapter: preview?.chapter,
      verse: preview?.verse,
      verse_ref: `${abbrev}.${preview?.chapter}.${preview?.verse}`,
      verse_text: preview?.text,
      bible_version: 'KJV'
    });
    setQuery('');
    setPreview(null);
  };

  return (
    <div className="bg-gray-800 border border-amber-500/30 rounded-lg p-4 mt-4">
      <h4 className="text-sm font-semibold text-amber-400 mb-3">Add Verse</h4>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e?.target?.value)}
          onKeyDown={e => e?.key === 'Enter' && handleSearch()}
          placeholder='e.g. "John 3:16"'
          className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
        />
        <button
          onClick={handleSearch}
          disabled={searching || !query?.trim()}
          className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm rounded font-medium flex items-center gap-1"
        >
          <Search className="w-4 h-4" />
          {searching ? 'Searching...' : 'Search'}
        </button>
        <button onClick={onCancel} className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded">
          <X className="w-4 h-4" />
        </button>
      </div>
      {searchError && <p className="text-red-400 text-xs mb-2">{searchError}</p>}
      {preview && (
        <div className="bg-gray-700 rounded p-3 mb-3">
          <p className="text-amber-300 text-xs font-semibold mb-1">
            {preview?.book_name} {preview?.chapter}:{preview?.verse} (KJV)
          </p>
          <p className="text-gray-200 text-sm leading-relaxed">{preview?.text}</p>
          <button
            onClick={handleConfirm}
            className="mt-3 px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded font-medium"
          >
            ✓ Add to List
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const VerseListManagement = () => {
  const navigate = useNavigate();
  const { userProfile, isDemoAdmin } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [listItems, setListItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddVerse, setShowAddVerse] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', topic: '', is_active: true });

  // Admin check
  useEffect(() => {
    const isAdminUser = userProfile?.role === 'admin' || isDemoAdmin;
    setIsAdmin(isAdminUser);
    if (!isAdminUser && userProfile !== null) {
      setTimeout(() => navigate('/admin-dashboard'), 1500);
    }
  }, [userProfile, isDemoAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) loadLists();
  }, [isAdmin]);

  const loadLists = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('curated_verse_lists')?.select(`
          id, title, description, topic, is_active, created_at,
          curated_verse_list_items(id)
        `)?.order('created_at', { ascending: false });
      if (error) throw error;
      setLists(data || []);
    } catch (err) {
      setError('Failed to load verse lists');
    } finally {
      setLoading(false);
    }
  };

  const loadListItems = async (listId) => {
    try {
      const { data, error } = await supabase?.from('curated_verse_list_items')?.select('*')?.eq('list_id', listId)?.order('display_order', { ascending: true });
      if (error) throw error;
      setListItems(data || []);
    } catch (err) {
      setError('Failed to load verse items');
    }
  };

  const handleSelectList = (list) => {
    setSelectedList(list);
    setForm({
      title: list?.title || '',
      description: list?.description || '',
      topic: list?.topic || '',
      is_active: list?.is_active ?? true
    });
    setShowAddVerse(false);
    loadListItems(list?.id);
  };

  const handleNewList = () => {
    setSelectedList({ id: null });
    setForm({ title: '', description: '', topic: '', is_active: true });
    setListItems([]);
    setShowAddVerse(false);
  };

  const handleSaveList = async () => {
    if (!form?.title?.trim()) {
      setError('Title is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (selectedList?.id) {
        const { error } = await supabase?.from('curated_verse_lists')?.update({ ...form, updated_at: new Date()?.toISOString() })?.eq('id', selectedList?.id);
        if (error) throw error;
        setSuccess('List saved!');
      } else {
        const { data, error } = await supabase?.from('curated_verse_lists')?.insert({ ...form })?.select()?.single();
        if (error) throw error;
        setSelectedList(data);
        setSuccess('List created!');
      }
      await loadLists();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to save list');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteList = async (listId) => {
    try {
      const { error } = await supabase?.from('curated_verse_lists')?.delete()?.eq('id', listId);
      if (error) throw error;
      if (selectedList?.id === listId) {
        setSelectedList(null);
        setListItems([]);
      }
      await loadLists();
      setDeleteConfirm(null);
      setSuccess('List deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete list');
    }
  };

  const handleToggleActive = async (list) => {
    try {
      const { error } = await supabase?.from('curated_verse_lists')?.update({ is_active: !list?.is_active, updated_at: new Date()?.toISOString() })?.eq('id', list?.id);
      if (error) throw error;
      if (selectedList?.id === list?.id) {
        setForm(prev => ({ ...prev, is_active: !list?.is_active }));
        setSelectedList(prev => ({ ...prev, is_active: !list?.is_active }));
      }
      await loadLists();
    } catch (err) {
      setError('Failed to toggle active status');
    }
  };

  const handleAddVerse = async (verseData) => {
    if (!selectedList?.id) {
      setError('Save the list first before adding verses');
      return;
    }
    try {
      const nextOrder = listItems?.length;
      const { error } = await supabase?.from('curated_verse_list_items')?.insert({
        list_id: selectedList?.id,
        ...verseData,
        display_order: nextOrder
      });
      if (error) throw error;
      await loadListItems(selectedList?.id);
      await loadLists();
      setShowAddVerse(false);
      setSuccess('Verse added!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to add verse');
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      const { error } = await supabase?.from('curated_verse_list_items')?.delete()?.eq('id', itemId);
      if (error) throw error;
      const updated = listItems?.filter(i => i?.id !== itemId);
      setListItems(updated);
      // Re-order
      await Promise.all(updated?.map((item, idx) =>
        supabase?.from('curated_verse_list_items')?.update({ display_order: idx })?.eq('id', item?.id)
      ));
      await loadLists();
    } catch (err) {
      setError('Failed to delete verse');
    }
  };

  // Drag-to-reorder
  const handleDragStart = (index) => setDragIndex(index);
  const handleDragOver = (e, index) => {
    e?.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    const reordered = [...listItems];
    const [moved] = reordered?.splice(dragIndex, 1);
    reordered?.splice(index, 0, moved);
    setListItems(reordered);
    setDragIndex(index);
  };
  const handleDragEnd = async () => {
    setDragIndex(null);
    try {
      await Promise.all(listItems?.map((item, idx) =>
        supabase?.from('curated_verse_list_items')?.update({ display_order: idx })?.eq('id', item?.id)
      ));
    } catch (err) {
      setError('Failed to save order');
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-gray-500 mx-auto mb-3" />
          <p className="text-gray-400">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <Link
            to="/admin-dashboard"
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors mb-3"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Admin Dashboard</span>
          </Link>
          <h1 className="text-2xl font-bold text-white">Verse List Management</h1>
          <p className="text-gray-400 mt-1">Create and manage curated verse lists for topics and themes</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-4 bg-red-900/40 border border-red-700 rounded-lg px-4 py-3 flex items-center justify-between">
            <span className="text-red-300 text-sm">{error}</span>
            <button onClick={() => setError('')}><X className="w-4 h-4 text-red-400" /></button>
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-900/40 border border-green-700 rounded-lg px-4 py-3">
            <span className="text-green-300 text-sm">{success}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ── Left Panel: List of curated lists ── */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
                <h2 className="text-sm font-semibold text-gray-200">Curated Lists</h2>
                <button
                  onClick={handleNewList}
                  className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded font-medium"
                >
                  <Plus className="w-3 h-3" />
                  New List
                </button>
              </div>

              {loading ? (
                <div className="p-6 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500 mx-auto" />
                </div>
              ) : lists?.length === 0 ? (
                <div className="p-6 text-center text-gray-500 text-sm">No lists yet. Create one!</div>
              ) : (
                <div className="divide-y divide-gray-700">
                  {lists?.map(list => {
                    const verseCount = list?.curated_verse_list_items?.length || 0;
                    const isSelected = selectedList?.id === list?.id;
                    return (
                      <div
                        key={list?.id}
                        onClick={() => handleSelectList(list)}
                        className={`px-4 py-3 cursor-pointer transition-colors ${isSelected ? 'bg-amber-900/30 border-l-2 border-amber-500' : 'hover:bg-gray-750'}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-white truncate">{list?.title}</p>
                            {list?.topic && <p className="text-xs text-amber-400 mt-0.5">{list?.topic}</p>}
                            <p className="text-xs text-gray-500 mt-0.5">{verseCount} verse{verseCount !== 1 ? 's' : ''}</p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={e => { e?.stopPropagation(); handleToggleActive(list); }}
                              title={list?.is_active ? 'Active — click to deactivate' : 'Inactive — click to activate'}
                              className="text-gray-400 hover:text-amber-400"
                            >
                              {list?.is_active
                                ? <ToggleRight className="w-5 h-5 text-green-400" />
                                : <ToggleLeft className="w-5 h-5 text-gray-500" />}
                            </button>
                            <button
                              onClick={e => { e?.stopPropagation(); setDeleteConfirm(list?.id); }}
                              className="text-gray-500 hover:text-red-400 p-1"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        {/* Delete confirmation */}
                        {deleteConfirm === list?.id && (
                          <div className="mt-2 bg-red-900/40 border border-red-700 rounded p-2" onClick={e => e?.stopPropagation()}>
                            <p className="text-xs text-red-300 mb-2">Delete this list and all its verses?</p>
                            <div className="flex gap-2">
                              <button onClick={() => handleDeleteList(list?.id)} className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded">Delete</button>
                              <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs rounded">Cancel</button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Right Panel: Editor ── */}
          <div className="lg:col-span-2">
            {!selectedList ? (
              <div className="bg-gray-800 rounded-xl border border-gray-700 p-12 text-center">
                <p className="text-gray-500">Select a list to edit, or create a new one</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* List Editor */}
                <div className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                  <h2 className="text-sm font-semibold text-gray-200 mb-4">
                    {selectedList?.id ? 'Edit List' : 'New List'}
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Title <span className="text-red-400">*</span></label>
                      <input
                        type="text"
                        value={form?.title}
                        onChange={e => setForm(prev => ({ ...prev, title: e?.target?.value }))}
                        placeholder="e.g. Sabbath Verses"
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Topic</label>
                      <input
                        type="text"
                        value={form?.topic}
                        onChange={e => setForm(prev => ({ ...prev, topic: e?.target?.value }))}
                        placeholder="e.g. Sabbath, Faith, Salvation"
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-amber-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Description</label>
                      <textarea
                        value={form?.description}
                        onChange={e => setForm(prev => ({ ...prev, description: e?.target?.value }))}
                        placeholder="Brief description of this verse collection..."
                        rows={3}
                        className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-sm text-white placeholder-gray-400 focus:outline-none focus:border-amber-500 resize-none"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <label className="text-xs font-medium text-gray-400">Active</label>
                      <button
                        onClick={() => setForm(prev => ({ ...prev, is_active: !prev?.is_active }))}
                        className="text-gray-400 hover:text-amber-400"
                      >
                        {form?.is_active
                          ? <ToggleRight className="w-6 h-6 text-green-400" />
                          : <ToggleLeft className="w-6 h-6 text-gray-500" />}
                      </button>
                      <span className="text-xs text-gray-500">{form?.is_active ? 'Visible to users' : 'Hidden from users'}</span>
                    </div>
                    <button
                      onClick={handleSaveList}
                      disabled={saving}
                      className="flex items-center gap-2 px-5 py-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm rounded font-medium"
                    >
                      <Save className="w-4 h-4" />
                      {saving ? 'Saving...' : 'Save List'}
                    </button>
                  </div>
                </div>

                {/* Verse Items */}
                {selectedList?.id && (
                  <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
                      <h2 className="text-sm font-semibold text-gray-200">
                        Verses <span className="text-amber-400 ml-1">({listItems?.length})</span>
                      </h2>
                      <button
                        onClick={() => setShowAddVerse(!showAddVerse)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs rounded font-medium"
                      >
                        <Plus className="w-3 h-3" />
                        Add Verse
                      </button>
                    </div>

                    {showAddVerse && (
                      <div className="px-6 pb-4">
                        <AddVersePanel onAdd={handleAddVerse} onCancel={() => setShowAddVerse(false)} />
                      </div>
                    )}

                    {listItems?.length === 0 ? (
                      <div className="px-6 py-8 text-center text-gray-500 text-sm">
                        No verses yet. Click "Add Verse" to get started.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-750 border-b border-gray-700">
                            <tr>
                              <th className="text-left px-4 py-2 text-xs font-medium text-gray-400 w-8">#</th>
                              <th className="text-left px-4 py-2 text-xs font-medium text-gray-400">Reference</th>
                              <th className="text-left px-4 py-2 text-xs font-medium text-gray-400">Text Preview</th>
                              <th className="text-left px-4 py-2 text-xs font-medium text-gray-400 w-16">Version</th>
                              <th className="text-right px-4 py-2 text-xs font-medium text-gray-400 w-12"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-700">
                            {listItems?.map((item, idx) => (
                              <tr
                                key={item?.id}
                                draggable
                                onDragStart={() => handleDragStart(idx)}
                                onDragOver={e => handleDragOver(e, idx)}
                                onDragEnd={handleDragEnd}
                                className={`hover:bg-gray-750 transition-colors ${dragIndex === idx ? 'opacity-50' : ''}`}
                              >
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1">
                                    <GripVertical className="w-3 h-3 text-gray-600 cursor-grab" />
                                    <span className="text-xs text-gray-500">{idx + 1}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-sm font-medium text-amber-300">
                                    {item?.book_abbrev} {item?.chapter}:{item?.verse}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-xs text-gray-300">
                                    {item?.verse_text ? item?.verse_text?.slice(0, 60) + (item?.verse_text?.length > 60 ? '…' : '') : '—'}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className="text-xs text-gray-500">{item?.bible_version}</span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    onClick={() => handleDeleteItem(item?.id)}
                                    className="text-gray-600 hover:text-red-400 p-1"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerseListManagement;
